import { ChevronRightIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import slugify from 'slugify';

import { getBlogPagination, getCategories } from '../../services/blog';
import { getAllCourse, getCoursesAllCategories } from '../../services/Courses';
import {
  getAllBookCategories,
  getAllBookListApi,
} from '../../services/Library/Books';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  type: string;
}

const ProductDropdown = ({ open, type }: Props) => {
  const [categoryList, setCategoryList] = useState<any[]>([]);
  const [productList, setProductList] = useState<any[]>([]);
  const [loadingRight, setLoadingRight] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const router = useRouter();
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const { ref, inView } = useInView();

  const fetchCategories = async () => {
    try {
      let res;
      if (type === 'books') {
        res = await getAllBookCategories();
        setCategoryList(
          res
            .filter((category: any) => category.bookCount >= 1)
            .map((category: any) => ({
              id: category?._id,
              name: category?.name,
              slug: category?.slug,
            })),
        );
      } else if (type === 'courses') {
        res = await getCoursesAllCategories();
        setCategoryList(res.data);
      } else if (type === 'schools') {
        res = await getCategories();
        setCategoryList(
          res?.data?.map((item: any) => ({
            id: item?.category?.id,
            name: item?.category?.name,
          })),
        );
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Fetch resources
  const fetchResources = async (pageNo = 1, reset = false) => {
    if (!selectedCategory) return;
    setLoadingRight(true);
    try {
      let res;
      if (type === 'books') {
        const params = {
          page: pageNo.toString(),
          limit: '10',
          categoriesId: selectedCategory?.id || null,
        };
        res = await getAllBookListApi(params);

        const { data } = res;

        const formattedBooks = data?.map((book: any) => ({
          _id: book?._id,
          title: book?.title,
          slug: book?.slug,
          type: book?.type,
        }));

        setProductList((prev) =>
          reset ? formattedBooks : [...prev, ...formattedBooks],
        );

        setTotal(res?.metadata?.total);
      } else if (type === 'courses') {
        const params = {
          page: pageNo.toString(),
          limit: '10',
          categoriesId: selectedCategory?.name || null,
        };
        res = await getAllCourse(params);

        const { data } = res;

        const formattedCourses = data?.map((course: any) => ({
          _id: course?.ecom_product_id,
          title: course?.name,
          slug: course?.slug,
        }));

        setProductList((prev) =>
          reset ? formattedCourses : [...prev, ...formattedCourses],
        );

        setTotal(res?.extra?.totalDataLength);
      } else if (type === 'schools') {
        const params = {
          page: pageNo.toString(),
          limit: 10,
          categoriesId: null,
          category_name: selectedCategory?.name,
        };
        res = await getBlogPagination(params);
        console.log(res, 'res from schools');

        const { data } = res;

        const formattedSchools = data?.data?.map((school: any) => ({
          _id: school?.id,
          title: school?.name,
          slug: school?.slug,
        }));
        console.log(formattedSchools, data, 'formattedBooks');

        setProductList((prev) =>
          reset ? formattedSchools : [...prev, ...formattedSchools],
        );

        setTotal(data?.total);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoadingRight(false);
    }
  };
  const handleHoverCategory = (category: any) => {
    setSelectedCategory(category);
    setPage(1);
    setProductList([]);
  };

  useEffect(() => {
    if (open) {
      fetchCategories();
      setSelectedCategory(null);
      setProductList([]);
      setPage(1);
    }
  }, [open, type]);

  useEffect(() => {
    if (selectedCategory) {
      fetchResources(1, true);
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (inView && !loadingRight && productList.length < total) {
      fetchResources(page + 1);
      setPage((p) => p + 1);
    }
  }, [inView]);

  const handleProductClick = (
    type: string,
    product: { slug: string; _id: string; type?: string },
    router: any,
  ) => {
    const slugId = `${product.slug}-${product._id}`;

    const routes: Record<string, string> = {
      books:
        product.type === 'short'
          ? `/books/${slugId}?short=true`
          : `/books/${slugId}/`,
      courses: `/courses/${product.slug}/?id=${product._id}`,
      schools: `/schools/${product.slug}/?school_id=${product._id}`,
    };

    if (routes[type]) {
      router.push(routes[type]);
    }
  };

  return (
    <div className='relative'>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{
              opacity: 0,
              y: 10,
              height: 0,
              width: !selectedCategory ? 300 : 645,
            }}
            animate={{
              opacity: 1,
              y: 0,
              height: 'auto',
              width: !selectedCategory ? 300 : 645,
            }}
            exit={{ opacity: 0, y: 10, height: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
              width: {
                type: 'spring',
                stiffness: 100,
                damping: 15,
              },
            }}
            className='absolute top-full -translate-x-[20%] max-h-[530px] left-0 mt-4 rounded-xl bg-black text-white shadow-xl overflow-hidden z-50'
            style={{ border: '1px solid #333' }}
          >
            <div className='flex'>
              <motion.div
                animate={{ flexBasis: selectedCategory ? '40%' : '100%' }}
                transition={{
                  duration: 0.3,
                  type: 'spring',
                  stiffness: 100,
                  damping: 15,
                }}
                className={clsx(
                  selectedCategory && 'border-r border-[#282829]',
                )}
                style={{ flexShrink: 0 }}
              >
                {/* <span
                  className={clsx(
                    'px-8 mt-[23px] flex text-xs capitalize text-[#504F53] font-medium justify-between  items-center  transition-colors',
                  )}
                >
                  Browse {type}
                </span>
                <div className='py-2 '>
                  {staticData?.leftColumnBrowse.map((category, index) => (
                    <motion.div
                      key={category.key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(`/${category.route}`);
                      }}
                      className={clsx(
                        'px-8 py-2 flex justify-between text-white items-center cursor-pointer hover:bg-[#282829] transition-colors',
                      )}
                    >
                      <div className='flex items-center font-light text-white text-sm'>
                        <span>{category.title}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <Divider className='border-[#282829] my-2' />
                <div className='py-2 '>
                  {staticData?.leftColumnTags.map((category, index) => (
                    <motion.div
                      key={category.key}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(`/${category.route}`);
                      }}
                      className={clsx(
                        'px-8 py-2 flex justify-between text-white items-center cursor-pointer hover:bg-[#282829] transition-colors',
                      )}
                    >
                      <div className='flex items-center font-light text-white text-sm'>
                        <span>{category.title}</span>
                        {'tag' in category && category.tag && (
                          <span
                            style={{
                              background:
                                'linear-gradient(180deg, #F9742C 0%, #F20486 100%)',
                            }}
                            className='ml-2 text-xs  text-white px-2.5 py-0.5 rounded-full'
                          >
                            {category.tag}
                          </span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
                <Divider className='border-[#282829] my-2' /> */}
                <div
                  style={{
                    borderRadius: 12,
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#FFFFFF99 transparent',
                  }}
                  className={clsx(
                    'py-2  overflow-y-auto overflow-x-hidden max-h-[350px]',
                  )}
                  ref={leftScrollRef}
                >
                  {categoryList?.map((category, index) => (
                    <motion.div
                      key={`${category.id}-${index}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={clsx(
                        selectedCategory?.id === category.id
                          ? 'text-[#FF7837]'
                          : 'text-white',
                        'px-8 py-2 flex justify-between  items-center cursor-pointer hover:text-[#FF7837]  transition-colors',
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(
                          `/${type}/shop/${slugify(category.name || '', { lower: true })}`,
                        );
                      }}
                      onMouseEnter={() => handleHoverCategory(category)}
                    >
                      <div className='flex items-center text-sm font-light '>
                        <span>{category.name}</span>
                      </div>
                      <ChevronRightIcon className='h-4 w-4 transition-transform' />
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              <AnimatePresence mode='sync'>
                {selectedCategory && (
                  <motion.div
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: '60%' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{
                      duration: 0.3,
                      ease: 'easeInOut',
                      opacity: { duration: 0.2 },
                      width: {
                        type: 'spring',
                        stiffness: 100,
                        damping: 15,
                      },
                    }}
                    style={{
                      borderRadius: 12,
                      overflowY: 'auto',
                      scrollbarWidth: 'thin',
                      scrollbarColor: '#FFFFFF99 transparent',
                    }}
                    className='py-2 h-[530px] overflow-y-auto overflow-x-hidden'
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className='px-8 py-2 mt-3.5 text-[#504F53] font-medium  text-xs'
                    >
                      {selectedCategory.name || type}
                    </motion.div>
                    <div className='mt-1'>
                      {productList.map((product, index) => (
                        <motion.a
                          key={`${product.title}-${index}`}
                          href={product.url}
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.03 }}
                          onClick={() =>
                            handleProductClick(type, product, router)
                          }
                          className={clsx(
                            'block px-8 py-2 hover:bg-[#450B05] text-white text-sm font-light  hover:text-[#FF7837]   transition-colors',
                          )}
                        >
                          {product.title}
                        </motion.a>
                      ))}
                      {productList.length < total && (
                        <div ref={ref} className='px-8 py-2 text-xs text-black'>
                          Loading more...
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductDropdown;
